import React from "react";

import { Backdrop, CircularProgress, Box, Typography } from "@mui/material";
import { keyframes } from "@mui/system";

const defaultCirceSize = 50;

// Keyframe animation for loading text wave effect
const waveAnimation = keyframes`
  0%, 40%, 100% {
    transform: translateY(0px);
    opacity: 0.6;
  }
  20% {
    transform: translateY(-10px);
    opacity: 1;
  }
`;

const dotAnimation = keyframes`
  0%, 20% {
    color: transparent;
  }
  50% {
    color: white;
  }
  100% {
    color: transparent;
  }
`;

// Loading text component with wave animation
const LoadingText = ({ text = "Loading", color = "#3457DC" }) => {
  const letters = text.split("");

  return (
    <Typography
      className="h2"
      sx={{
        color: color,
        fontWeight: 500,
        marginTop: 2,
        display: "flex",
        alignItems: "center",
        "& .letter": {
          display: "inline-block",
          animation: `${waveAnimation} 1.4s infinite ease-in-out`,
        },
        "& .dot": {
          display: "inline-block",
          animation: `${dotAnimation} 1.4s infinite ease-in-out`,
        },
      }}
    >
      {letters.map((letter, index) => (
        <span
          key={index}
          className="letter"
          style={{
            animationDelay: `${index * 0.1}s`,
            marginRight: letter === " " ? "0.5ch" : "0",
          }}
        >
          {letter === " " ? "\u00A0" : letter}
        </span>
      ))}
      <span
        className="dot"
        style={{ animationDelay: `${letters.length * 0.1}s` }}
      >
        .
      </span>
      <span
        className="dot"
        style={{ animationDelay: `${(letters.length + 1) * 0.1}s` }}
      >
        .
      </span>
      <span
        className="dot"
        style={{ animationDelay: `${(letters.length + 2) * 0.1}s` }}
      >
        .
      </span>
    </Typography>
  );
};

export const Loader = ({
  size = defaultCirceSize,
  showText = true,
  text = "Loading",
}) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <CircularProgress
        size={size}
        sx={{
          color: "#3457DC",
        }}
      />
      {showText && <LoadingText text={text} />}
    </Box>
  );
};

export const BackdropLoader = ({
  open,
  size = defaultCirceSize,
  children,
  showText = true,
  text = "Loading",
}) => {
  return (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 150 }}
      open={open}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress
          color="inherit"
          size={size}
          sx={{
            color: "#3457DC",
          }}
        />
        {showText && <LoadingText text={text} color="#fff" />}
      </Box>
      {children}
    </Backdrop>
  );
};

export default Loader;
